/* WebAuthn / passkey helpers — base64url <-> ArrayBuffer and orchestration. */
import { api } from "@/lib/api";

function b64urlToBuf(b64url) {
    const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
    const b64 = (b64url + pad).replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return buf.buffer;
}

function bufToB64url(buf) {
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export const isWebAuthnSupported = () => !!(window?.PublicKeyCredential && navigator.credentials?.create);

export async function registerPasskey(deviceName) {
    const { data: opts } = await api.post("/webauthn/register/options");
    opts.challenge = b64urlToBuf(opts.challenge);
    opts.user.id = b64urlToBuf(opts.user.id);
    if (opts.excludeCredentials) {
        opts.excludeCredentials = opts.excludeCredentials.map(c => ({ ...c, id: b64urlToBuf(c.id) }));
    }
    const cred = await navigator.credentials.create({ publicKey: opts });
    if (!cred) throw new Error("Registration cancelled");
    const r = cred.response;
    const payload = {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        response: {
            clientDataJSON: bufToB64url(r.clientDataJSON),
            attestationObject: bufToB64url(r.attestationObject),
        },
    };
    const { data } = await api.post("/webauthn/register/verify", { credential: payload, device_name: deviceName || undefined });
    return data;
}

export async function loginWithPasskey(email) {
    const { data: opts } = await api.post("/webauthn/login/options", { email });
    opts.challenge = b64urlToBuf(opts.challenge);
    if (opts.allowCredentials) {
        opts.allowCredentials = opts.allowCredentials.map(c => ({ ...c, id: b64urlToBuf(c.id) }));
    }
    const assertion = await navigator.credentials.get({ publicKey: opts });
    if (!assertion) throw new Error("Login cancelled");
    const r = assertion.response;
    const payload = {
        id: assertion.id,
        rawId: bufToB64url(assertion.rawId),
        type: assertion.type,
        response: {
            clientDataJSON: bufToB64url(r.clientDataJSON),
            authenticatorData: bufToB64url(r.authenticatorData),
            signature: bufToB64url(r.signature),
            userHandle: r.userHandle ? bufToB64url(r.userHandle) : null,
        },
    };
    const { data } = await api.post("/webauthn/login/verify", { email, credential: payload });
    return data;
}

export async function checkPasskeyAvailable(email) {
    try {
        const { data } = await api.get(`/webauthn/available`, { params: { email } });
        return data.available;
    } catch { return false; }
}
